import { CommonModule } from "@angular/common";
import { AfterViewInit, Component, OnDestroy, OnInit } from "@angular/core";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { FieldType, FieldTypeConfig } from "@ngx-formly/core";
import { TreeNode } from "primeng/api";
import { TreeModule } from "primeng/tree";
import { Subscription } from "rxjs";

/* 
  Example usage:
  {
    key: "permissions",
    type: "treeCheckbox",
    props: {
      label: {
        en: "Permissions",
        fi: "Käyttöoikeudet",
        sv: "Behörigheter",
      },
      description: {
        en: "Select the permissions for this user",
        fi: "Valitse tämän käyttäjän oikeudet",
        sv: "Välj behörigheter för den här användaren",
      },
      expandAll: true,
      options: [
        {
          key: 111,
          label: {
            en: "Accounting",
            fi: "Kirjanpito",
            sv: "Bokföring",
          },
          icon: "pi pi-cog",
          children: [
            {
              key: 1,
              label: {
                en: "Account Material Delivery",
                fi: "Kirjanpidon materiaalien toimittaminen",
                sv: "Bokföringsmaterialleverans",
              },
              icon: "pi pi-users",
            },
          ],
        },
        {
          key: 222,
          label: {
            en: "Payroll",
            fi: "Palkkahallinto",
            sv: "Lönehantering",
          },
          icon: "pi pi-file",
          children: [
            {
              key: 3,
              label: {
                en: "Empoyee Document Delivery",
                fi: "Työntekijädokumenttien toimittaminen",
                sv: "Leverans av anställdas dokument",
              },
              icon: "pi pi-plus",
            },
            {
              key: 4,
              label: {
                en: "HR Changes",
                fi: "Henkilöstömuutokset",
                sv: "HR-förändringar",
              },
              icon: "pi pi-upload",
            },
          ],
        },
      ],
    },
    defaultValue: [111, 1, 3], // Pre-select these nodes
  },


  ////////////////////

  JSON example:

  {
    "key": "permissions",
    "type": "treeCheckbox",
    "props": {
      "label": {
        "en": "Permissions",
        "fi": "Käyttöoikeudet",
        "sv": "Behörigheter"
      },
      "description": {
        "en": "Select the permissions for this user",
        "fi": "Valitse tämän käyttäjän oikeudet",
        "sv": "Välj behörigheter för den här användaren"
      },
      "expandAll": true,
      "options": [
        {
          "key": 111,
          "label": {
            "en": "Accounting",
            "fi": "Kirjanpito",
            "sv": "Bokföring"
          },
          "icon": "pi pi-cog",
          "children": [
            {
              "key": 1,
              "label": {
                "en": "Account Material Delivery",
                "fi": "Kirjanpidon materiaalien toimittaminen",
                "sv": "Bokföringsmaterialleverans"
              },
              "icon": "pi pi-users"
            }
          ]
        },
        {
          "key": 222,
          "label": {
            "en": "Payroll",
            "fi": "Palkkahallinto",
            "sv": "Lönehantering"
          },
          "icon": "pi pi-file",
          "children": [
            {
              "key": 3,
              "label": {
                "en": "Employee Document Delivery",
                "fi": "Työntekijädokumenttien toimittaminen",
                "sv": "Leverans av anställdas dokument"
              },
              "icon": "pi pi-plus"
            },
            {
              "key": 4,
              "label": {
                "en": "HR Changes",
                "fi": "Henkilöstömuutokset",
                "sv": "HR-förändringar"
              },
              "icon": "pi pi-upload"
            }
          ]
        }
      ]
    },
    "defaultValue": [111, 1, 3] // Pre-select these nodes
  }
*/

@Component({
  selector: "app-formly-tree-checkbox",
  templateUrl: "./tree-checkbox.type.html",
  standalone: true,
  imports: [CommonModule, TreeModule, TranslocoDirective],
})
export class TreeCheckboxComponent extends FieldType<FieldTypeConfig> implements OnInit, OnDestroy, AfterViewInit {
  // TreeNodes for the p-tree component
  treeNodes: TreeNode[] = [];

  // Currently selected nodes
  selectedNodes: TreeNode[] = [];

  /**
   * Check if field is in invalid state and should show validation messages
   */
  get isInvalid(): boolean {
    return this.formControl.invalid && (this.formControl.dirty || this.formControl.touched);
  }

  // Subscription to cleanup on destroy
  private subscription: Subscription | null = null;

  constructor(private translocoService: TranslocoService) {
    super();
  }

  ngOnInit(): void {
    // Initialize tree nodes from props or use empty array
    if (this.props.options) {
      if ("subscribe" in this.props.options) {
        // Handle observable case
        this.subscription = this.props.options.subscribe((nodes: TreeNode[]) => {
          this.treeNodes = nodes;
          // Initialize selection after we have nodes
          if (this.formControl.value) {
            this.initializeSelection();
          }
        });
      } else {
        // Handle array case
        this.treeNodes = this.props.options as TreeNode[];
        // Set initial selection based on form control value (if any)
        if (this.formControl.value) {
          this.initializeSelection();
        }
      }
    } else {
      this.treeNodes = [];
      // Set initial selection based on form control value (if any)
      if (this.formControl.value) {
        this.initializeSelection();
      }
    }

    // Formly sets the 'disabled' attribute on the <p-tree> element, but p-tree ignores that.
    // So instead, we watch for the formControl itself to be disabled, and manually enable/disable the individual
    // tree nodes.
    this.formControl.registerOnDisabledChange((isDisabled) => {
      this.setEnabledRecursive(this.treeNodes, !isDisabled);
    });
  }

  ngAfterViewInit(): void {
    // Process nodes to ensure they start expanded
    if (this.props["expandAll"]) {
      this.ensureNodesExpanded(this.treeNodes);
    }
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  private ensureNodesExpanded(nodes: TreeNode[]): void {
    if (!nodes) {
      return;
    }

    for (const node of nodes) {
      // Set expanded to true for all nodes
      node.expanded = true;

      // Process children recursively
      if (node.children && node.children.length > 0) {
        this.ensureNodesExpanded(node.children);
      }
    }
  }

  /**
   * Called when selection changes in the tree
   */
  onSelectionChange(): void {
    // Extract keys/values from selected nodes to update form control
    const selectedValues = this.selectedNodes.map((node) => node.key);
    this.formControl.setValue(selectedValues);
    this.formControl.markAsDirty();
  }

  /**
   * Initialize selected nodes from form control value
   */
  private initializeSelection(): void {
    const selectedKeys = this.formControl.value || [];
    this.selectedNodes = this.findNodesByKeys(this.treeNodes, selectedKeys);
  }

  private findNodesByKeys(nodes: TreeNode[], keys: (string | number)[]): TreeNode[] {
    const result: TreeNode[] = [];

    const searchInNodes = (nodeList: TreeNode[]) => {
      for (const node of nodeList) {
        if (node.key !== undefined && keys.includes(node.key as string | number)) {
          result.push(node);
        }

        if (node.children && node.children.length > 0) {
          searchInNodes(node.children);
        }
      }
    };

    searchInNodes(nodes);
    return result;
  }

  private setEnabledRecursive(nodes: TreeNode[], isEnabled: boolean): void {
    for (const node of nodes) {
      node.selectable = isEnabled;
      if (node.children) {
        this.setEnabledRecursive(node.children, isEnabled);
      }
    }
  }
}
